var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, Paper, Stack, Typography, styled } from '@mui/material';
import { isEqual, omit, pick } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ErrorMessageBox, FallbackComponent, getLOBName, getUnassignedLOB, sortByLOBName } from '../common';
import { useReportOutletContext } from '../report/hooks';
import { useForbiddenRedirect, useReportAPI } from '../report/selectors';
import { useExportAPI, usePrintTableAPI, useTrainingReportAPI, useUpdateHeaderTrainingAPI, useUpdateTableTrainingAPI, } from './selectors';
import { SummaryDialog } from './summary';
import { TrainingRecruitmentForm } from './trainingForm';
import { TrainingTable } from './trainingTable';
export var convertTrainingsForAbsoluteMode = function (trainings) {
    // forecastAttritionRate is frontend-only (synced display field), never sent to the API.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return trainings.map(function (_a) {
        var forecastedAgentAttrition = _a.forecastedAgentAttrition, _fRate = _a.forecastAttritionRate, rest = __rest(_a, ["forecastedAgentAttrition", "forecastAttritionRate"]);
        if (forecastedAgentAttrition != null) {
            return __assign(__assign({}, rest), { adjustedAttritionRate: forecastedAgentAttrition });
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        var adjustedAttritionRate = rest.adjustedAttritionRate, withoutRate = __rest(rest, ["adjustedAttritionRate"]);
        return withoutRate;
    });
};
export var LeftPanel = styled(Paper)(function (_a) {
    var theme = _a.theme;
    return ({
        position: 'relative',
        padding: theme.spacing(8),
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.primary.light,
        height: '100%',
        overflow: 'auto',
    });
});
export var useDefaultValues = function (currentScenarioId, currentQueue, currentQueueLocation) {
    return useMemo(function () {
        var _a, _b, _c;
        var activeTrainings = (_b = (_a = currentQueueLocation === null || currentQueueLocation === void 0 ? void 0 : currentQueueLocation.trainings) !== null && _a !== void 0 ? _a : currentQueue === null || currentQueue === void 0 ? void 0 : currentQueue.trainings) !== null && _b !== void 0 ? _b : [];
        var trainingHeader = activeTrainings.find(function (item) { return item.month === -1; });
        return {
            scenarioId: currentScenarioId,
            queueId: currentQueue === null || currentQueue === void 0 ? void 0 : currentQueue.id,
            queueLocationId: (_c = currentQueueLocation === null || currentQueueLocation === void 0 ? void 0 : currentQueueLocation.id) !== null && _c !== void 0 ? _c : null,
            trainingLength: trainingHeader === null || trainingHeader === void 0 ? void 0 : trainingHeader.trainingLength,
            forecastedGraduationRate: trainingHeader === null || trainingHeader === void 0 ? void 0 : trainingHeader.forecastedGraduationRate,
            classSize: trainingHeader === null || trainingHeader === void 0 ? void 0 : trainingHeader.classSize,
            recruitingLeadTime: trainingHeader === null || trainingHeader === void 0 ? void 0 : trainingHeader.recruitingLeadTime,
            trainings: activeTrainings
                .filter(function (item) { return item.month !== -1; })
                .map(function (_a) {
                var id = _a.id, queueLocationId = _a.queueLocationId, trainingLength = _a.trainingLength, forecastedGraduationRate = _a.forecastedGraduationRate, classSize = _a.classSize, recruitingLeadTime = _a.recruitingLeadTime, adjustedAttritionRate = _a.adjustedAttritionRate, forecastedAgentAttrition = _a.forecastedAgentAttrition, forecastAttritionRate = _a.forecastAttritionRate;
                return ({
                    id: id,
                    queueLocationId: queueLocationId,
                    trainingLength: trainingLength,
                    forecastedGraduationRate: forecastedGraduationRate,
                    classSize: classSize,
                    recruitingLeadTime: recruitingLeadTime,
                    adjustedAttritionRate: adjustedAttritionRate,
                    forecastedAgentAttrition: forecastedAgentAttrition,
                    forecastAttritionRate: forecastAttritionRate,
                });
            }),
        };
    }, [
        currentQueue === null || currentQueue === void 0 ? void 0 : currentQueue.id,
        currentQueue === null || currentQueue === void 0 ? void 0 : currentQueue.trainings,
        currentQueueLocation === null || currentQueueLocation === void 0 ? void 0 : currentQueueLocation.id,
        currentQueueLocation === null || currentQueueLocation === void 0 ? void 0 : currentQueueLocation.trainings,
        currentScenarioId,
    ]);
};
export var TrainingRecruitment = function () {
    var _a, _b, _c, _d, _e;
    var t = useTranslation(['report']).t;
    var _f = useState(false), openSummary = _f[0], setOpenSummary = _f[1];
    var _g = useState(), currentScenarioId = _g[0], setCurrentScenarioId = _g[1];
    var _h = useState(), currentQueueId = _h[0], setCurrentQueueId = _h[1];
    var _j = useState(), currentQueueLocationId = _j[0], setCurrentQueueLocationId = _j[1];
    var _k = useReportAPI(), reportData = _k[0], fetchReport = _k[1];
    var printData = usePrintTableAPI(currentScenarioId)[0];
    var exportData = useExportAPI()[0];
    var _l = useTrainingReportAPI(currentScenarioId), trainingReportData = _l[0], fetchTrainingReport = _l[1], clearTrainingReport = _l[2];
    var _m = useUpdateHeaderTrainingAPI(), updateHeaderTrainingData = _m[0], updateHeaderTraining = _m[1], clearUpdateHeaderTrainingData = _m[2];
    var _o = useUpdateTableTrainingAPI(), updateTableTrainingData = _o[0], updateTableTraining = _o[1], clearUpdateTableTrainingData = _o[2];
    useForbiddenRedirect([
        trainingReportData.statusCode,
        updateHeaderTrainingData.statusCode,
        updateTableTrainingData.statusCode,
        exportData.statusCode,
        printData.statusCode,
    ]);
    var currenScenario = (_b = (_a = reportData.data) === null || _a === void 0 ? void 0 : _a.scenarios) === null || _b === void 0 ? void 0 : _b.find(function (sc) { return sc.id === currentScenarioId; });
    var enrichedQueueData = useMemo(function () {
        var _a, _b;
        var queues = (_b = (_a = trainingReportData.data) === null || _a === void 0 ? void 0 : _a.queues) !== null && _b !== void 0 ? _b : [];
        var unassigned = getUnassignedLOB();
        return queues
            .map(function (q) { return (__assign(__assign({}, q), { lobName: getLOBName(q.lobName, q.subLobName, unassigned) })); })
            .sort(function (a, b) { return sortByLOBName(a, b, unassigned); });
    }, [(_c = trainingReportData.data) === null || _c === void 0 ? void 0 : _c.queues]);
    var currentQueue = enrichedQueueData.find(function (sc) { return sc.id === currentQueueId; });
    var currentQueueLocation = (_d = currentQueue === null || currentQueue === void 0 ? void 0 : currentQueue.queueLocations) === null || _d === void 0 ? void 0 : _d.find(function (location) { return location.id === currentQueueLocationId; });
    var activeTrainings = (_e = currentQueueLocation === null || currentQueueLocation === void 0 ? void 0 : currentQueueLocation.trainings) !== null && _e !== void 0 ? _e : currentQueue === null || currentQueue === void 0 ? void 0 : currentQueue.trainings;
    var trainingDataTable = activeTrainings === null || activeTrainings === void 0 ? void 0 : activeTrainings.filter(function (item) { return item.month !== -1; });
    var isDeferrableQueue = currentQueue === null || currentQueue === void 0 ? void 0 : currentQueue.isDeferrable;
    var isAbandonableQueue = currentQueue === null || currentQueue === void 0 ? void 0 : currentQueue.isAbandonable;
    var context = useReportOutletContext();
    var defaultValues = useDefaultValues(currentScenarioId, currentQueue, currentQueueLocation);
    var validateForm = yup.object().shape({
        scenarioId: yup.number(),
        queueId: yup.number(),
        queueLocationId: yup.number().nullable(),
        trainingLength: yup.number().nullable(),
        forecastedGraduationRate: yup.number().nullable(),
        classSize: yup.number().nullable(),
        recruitingLeadTime: yup.number().nullable(),
        trainings: yup
            .array()
            .of(yup.object().shape({
            id: yup.number().nullable(),
            queueLocationId: yup.number().nullable(),
            trainingLength: yup.number().nullable(),
            forecastedGraduationRate: yup.number().nullable(),
            classSize: yup.number().nullable(),
            recruitingLeadTime: yup.number().nullable(),
            adjustedAttritionRate: yup.number().nullable(),
            forecastedAgentAttrition: yup.number().nullable(),
            forecastAttritionRate: yup.number().nullable(),
        }))
            .required(),
    });
    var _p = useForm({
        defaultValues: defaultValues,
        resolver: yupResolver(validateForm),
    }), control = _p.control, reset = _p.reset, handleSubmit = _p.handleSubmit, setValue = _p.setValue, watch = _p.watch;
    var _q = watch(), trainingLength = _q.trainingLength, forecastedGraduationRate = _q.forecastedGraduationRate, classSize = _q.classSize, recruitingLeadTime = _q.recruitingLeadTime, trainings = _q.trainings;
    var isEmptyHeaderForm = trainingLength === null || !forecastedGraduationRate || !classSize || !recruitingLeadTime;
    var isEmptyTableForm = trainings === null || trainings === void 0 ? void 0 : trainings.some(function (row) { return Object.values(omit(row, 'queueLocationId')).includes(null); });
    var isHeaderFormChange = !isEqual({ trainingLength: trainingLength, forecastedGraduationRate: forecastedGraduationRate, classSize: classSize, recruitingLeadTime: recruitingLeadTime }, pick(defaultValues, ['trainingLength', 'forecastedGraduationRate', 'classSize', 'recruitingLeadTime']));
    var isTableFormChange = !isEqual(trainings, defaultValues.trainings);
    var isEnableSubmit = function () {
        if (isHeaderFormChange && !isEmptyHeaderForm) {
            return true;
        }
        if (isTableFormChange && !isEmptyTableForm && !isEmptyHeaderForm) {
            return true;
        }
        return false;
    };
    useEffect(function () {
        fetchReport();
    }, [fetchReport]);
    useEffect(function () {
        var _a;
        if (reportData.data) {
            var defaultScenario = (_a = reportData.data.scenarios) === null || _a === void 0 ? void 0 : _a[0].id;
            setCurrentScenarioId(defaultScenario);
        }
    }, [reportData.data, setValue]);
    useEffect(function () {
        fetchTrainingReport();
        return function () {
            clearTrainingReport();
            clearUpdateHeaderTrainingData();
            clearUpdateTableTrainingData();
        };
    }, [fetchTrainingReport, clearTrainingReport, clearUpdateHeaderTrainingData, clearUpdateTableTrainingData]);
    useEffect(function () {
        var _a, _b;
        (_a = context === null || context === void 0 ? void 0 : context.setIsDisabledExport) === null || _a === void 0 ? void 0 : _a.call(context, true);
        if (currentScenarioId) {
            setCurrentQueueId(undefined);
            setCurrentQueueLocationId(undefined);
            (_b = context === null || context === void 0 ? void 0 : context.setIsDisabledExport) === null || _b === void 0 ? void 0 : _b.call(context, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentScenarioId]);
    useEffect(function () {
        if (enrichedQueueData.length > 0) {
            var defaultQueue = currentQueueId !== null && currentQueueId !== void 0 ? currentQueueId : enrichedQueueData[0].id;
            setCurrentQueueId(defaultQueue);
        }
    }, [currentQueueId, enrichedQueueData]);
    useEffect(function () {
        var _a;
        var locations = (_a = currentQueue === null || currentQueue === void 0 ? void 0 : currentQueue.queueLocations) !== null && _a !== void 0 ? _a : [];
        var currentLocationExists = locations.some(function (location) { return location.id === currentQueueLocationId; });
        var selectedLocationId = locations.length === 0 ? undefined : (currentLocationExists && currentQueueLocationId) || locations[0].id;
        setCurrentQueueLocationId(selectedLocationId);
    }, [currentQueue, currentQueueLocationId]);
    useEffect(function () {
        reset(defaultValues);
    }, [reset, defaultValues]);
    useEffect(function () {
        if (updateHeaderTrainingData.data) {
            fetchTrainingReport();
        }
    }, [fetchTrainingReport, updateHeaderTrainingData.data]);
    useEffect(function () {
        if (updateTableTrainingData.data) {
            fetchTrainingReport();
        }
    }, [fetchTrainingReport, updateTableTrainingData.data]);
    var handleUpdateTrainingRecruitment = handleSubmit(function (values) {
        if (isHeaderFormChange) {
            updateHeaderTraining(omit(values, ['scenarioId', 'trainings']));
            return;
        }
        if (isTableFormChange) {
            var payload = pick(values, ['queueId', 'queueLocationId', 'trainings']);
            if (payload.trainings) {
                // Strip display-only fields not in TrainingDTO; the API rejects unknown fields.
                payload.trainings = payload.trainings.map(function (training) {
                    return omit(training, ['forecastedAgentAttrition', 'forecastAttritionRate']);
                });
            }
            updateTableTraining(payload);
        }
    });
    return (_jsxs(_Fragment, { children: [_jsx(ErrorMessageBox, { errors: [printData.error, exportData.error] }), _jsx(FallbackComponent, __assign({ overlay: true, requesting: !!trainingReportData.requesting || !!reportData.requesting }, { children: _jsxs(Box, __assign({ padding: 5, component: "form", onSubmit: handleUpdateTrainingRecruitment }, { children: [_jsxs(Grid, __assign({ container: true, spacing: 5 }, { children: [_jsx(Grid, __assign({ item: true, xs: 4, lg: 3 }, { children: _jsx(LeftPanel, { children: _jsx(TrainingRecruitmentForm, { reportData: reportData.data, queueData: enrichedQueueData, locationData: currentQueue === null || currentQueue === void 0 ? void 0 : currentQueue.queueLocations, control: control, disableButton: !isEnableSubmit(), setCurrentScenario: setCurrentScenarioId, setCurrentQueue: setCurrentQueueId, setCurrentQueueLocation: setCurrentQueueLocationId }) }) })), _jsx(Grid, __assign({ item: true, xs: 8, lg: 9 }, { children: _jsxs(Stack, __assign({ gap: 4 }, { children: [_jsxs(Stack, __assign({ gap: 4, direction: "row", justifyContent: "space-between", alignItems: "center" }, { children: [_jsx(Typography, { children: t('training.trainingDescriptions') }), _jsx(Button, __assign({ onClick: function () { return setOpenSummary(true); }, sx: { minWidth: '10rem' }, variant: "outlined" }, { children: t('training.viewSummary') }))] })), trainingDataTable && currenScenario && currentQueue && (_jsx(TrainingTable, { currentScenario: currenScenario, trainings: trainingDataTable, control: control, setValue: setValue, isDeferrableQueue: isDeferrableQueue, isAbandonableQueue: isAbandonableQueue, attritionEntryMode: currentQueue === null || currentQueue === void 0 ? void 0 : currentQueue.attritionEntryMode }))] })) }))] })), _jsx(SummaryDialog, { open: openSummary, onClose: function () { return setOpenSummary(false); }, scenarioId: currentScenarioId })] })) }))] }));
};
